/**
 * Floating banner shown when the user has an active order
 * and navigates back to the menu or pre-menu page.
 *
 * Subscribes to real-time order updates via Firebase RTDB so
 * the status stays current even when not on the tracking page.
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, X } from 'lucide-react'
import { Button } from '@/components/ui'
import { useAppSelector } from '@/store/hooks'
import { useOrderTracking } from '@/hooks/useOrderTracking'
import type { OrderStatus } from '@/types/order'

interface ActiveOrderBannerProps {
  onViewOrder: () => void
}

const ACTIVE_STATUSES: OrderStatus[] = ['confirmed', 'preparing', 'ready']

const STATUS_CONFIG: Record<string, { label: string; dotColor: string; pulse: boolean }> = {
  confirmed: {
    label: 'Order Confirmed',
    dotColor: 'bg-blue-500',
    pulse: false,
  },
  preparing: {
    label: 'Preparing',
    dotColor: 'bg-amber-500',
    pulse: true,
  },
  ready: {
    label: 'Ready for Pickup',
    dotColor: 'bg-emerald-500',
    pulse: false,
  },
}

export function ActiveOrderBanner({ onViewOrder }: ActiveOrderBannerProps) {
  const orderStatus = useAppSelector((s) => s.order.orderStatus)
  const orderNumber = useAppSelector((s) => s.order.orderNumber)
  const orderId = useAppSelector((s) => s.order.orderId)
  const trackingCode = useAppSelector((s) => s.order.trackingCode)
  const [dismissed, setDismissed] = useState(false)

  const isActive = ACTIVE_STATUSES.includes(orderStatus)

  // Subscribe to real-time updates so banner reflects live status
  useOrderTracking({
    orderId,
    trackingCode,
    pollingInterval: 15000,
    enabled: isActive,
  })

  const config = STATUS_CONFIG[orderStatus]
  const visible = isActive && !dismissed

  return (
    <AnimatePresence>
      {visible && config && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-4 pointer-events-none"
          style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          <div className="mx-auto max-w-lg pointer-events-auto">
            <div
              className="
                relative flex items-center gap-3 px-4 py-3
                rounded-2xl border border-border/60
                bg-card/95 backdrop-blur-lg
                shadow-lg shadow-black/5
              "
            >
              {/* Status dot */}
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                {config.pulse && (
                  <span
                    className={`absolute inset-0 rounded-full ${config.dotColor} animate-ping opacity-75`}
                  />
                )}
                <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${config.dotColor}`} />
              </span>

              {/* Status text + order number */}
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-foreground">
                  {config.label}
                </span>
                {orderNumber && (
                  <span className="ml-2 text-sm text-muted-foreground font-mono">
                    #{orderNumber}
                  </span>
                )}
              </div>

              {/* View Order button */}
              <Button
                size="sm"
                onClick={onViewOrder}
                className="rounded-full px-4 text-xs font-semibold active:scale-95 transition-all"
              >
                View Order
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>

              {/* Dismiss button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setDismissed(true)}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-muted border border-border text-muted-foreground hover:text-foreground"
                aria-label="Dismiss order banner"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
